import type { Database } from 'node-sqlite3-wasm';
import { removeSqliteLockPathIfPresent } from '../sqlite-lock-path.js';
import type { SqliteDatabaseAccess, SqliteDatabaseAccessOptions } from '../database-access-types.js';
import { ASYNC_PROBE_RETRY_MAX } from '../sqlite-lock-retry.js';
import { createSqliteMigrateState } from './bindings.js';
import { openSqliteConnection, safeCloseSqliteDatabase } from './open.js';
import { runSqliteWithAsync } from './with-async.js';
import { runSqliteWithSync } from './with-sync.js';

export type { SqliteDatabaseAccess, SqliteDatabaseAccessOptions } from '../database-access-types.js';

export function createSqliteDatabaseAccess<OpenOptions = void>(
  config: SqliteDatabaseAccessOptions<OpenOptions>,
): SqliteDatabaseAccess<OpenOptions> {
  const migrateState = createSqliteMigrateState();

  function safeClose(db: Database | null | undefined): void {
    safeCloseSqliteDatabase(db);
  }

  function openConnection(options?: OpenOptions): Database {
    return openSqliteConnection(config, migrateState, options);
  }

  function withSync<T>(fn: (db: Database) => T, options?: OpenOptions): T {
    return runSqliteWithSync(config, openConnection, safeClose, fn, options);
  }

  async function withAsync<T>(
    fn: (db: Database) => T | Promise<T>,
    options?: OpenOptions,
    maxAttempts?: number,
  ): Promise<T> {
    return runSqliteWithAsync(config, openConnection, safeClose, fn, options, maxAttempts);
  }

  async function withAsyncProbe<T>(
    fn: (db: Database) => T | Promise<T>,
    options?: OpenOptions,
  ): Promise<T> {
    return runSqliteWithAsync(
      config,
      openConnection,
      safeClose,
      fn,
      options,
      ASYNC_PROBE_RETRY_MAX,
    );
  }

  async function ensureInitialized(options?: OpenOptions): Promise<void> {
    await withAsync(() => undefined, options);
  }

  function closeAllConnections(): void {
    migrateState.migrateDoneForPath = null;
    removeSqliteLockPathIfPresent(config.getPath());
  }

  return {
    safeClose,
    openConnection,
    withSync,
    withAsync,
    withAsyncProbe,
    ensureInitialized,
    closeAllConnections,
  };
}
